import type {
  DomainInput,
  DomainKey,
  FranceMonthInput,
  GroupKey,
  GroupMeta,
  PpaInput,
  PpaKey,
  StatementFixture,
  YearOption,
} from "./complianceGroupsTypes";

export const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
export const DEMO_YEAR = 2026;
/** September 2026: January to August have elapsed. */
export const NOW_MONTH_INDEX = 8;
/** Domain-wide default: actual consumption data ends 31 July 2026. */
export const REPORTED_THROUGH = 7;
export const MONTH_END = [
  "31",
  "28",
  "31",
  "30",
  "31",
  "30",
  "31",
  "31",
  "30",
  "31",
  "30",
  "31",
];
export const MONTHLY_DUE = [
  "28 Feb 2026",
  "31 Mar 2026",
  "30 Apr 2026",
  "31 May 2026",
  "30 Jun 2026",
  "31 Jul 2026",
  "31 Aug 2026",
  "30 Sep 2026",
  "31 Oct 2026",
  "30 Nov 2026",
  "31 Dec 2026",
  "31 Jan 2027",
];
export const QUARTERLY_DUE = [
  "30 Apr 2026",
  "31 Jul 2026",
  "31 Oct 2026",
  "31 Jan 2027",
];

const flat = (v: number): number[] => Array(12).fill(v);
const FR_PARIS = [
  9900, 6700, 7000, 6800, 7100, 7300, 7500, 7700, 7600, 7400, 7200, 7000,
];
const DE_FRANKFURT = [
  6300, 6400, 6500, 6600, 6700, 6800, 6900, 7000, 6900, 6800, 6700, 6600,
];
const SE_STOCKHOLM = [
  5000, 5100, 5200, 5300, 5400, 5500, 5600, 5700, 5600, 5500, 5400, 5300,
];
const ES_MADRID = [
  6000, 5900, 6100, 6200, 6300, 6500, 6700, 6900, 6800, 6500, 6200, 6000,
];

const PURPOSE =
  "Renewable electricity consumption of {beneficiary_name} at {address}";

/**
 * Split a monthly series into parts by share, keeping every part a round hundred and every
 * month's parts summing exactly to the original (the last part absorbs the rounding).
 */
export const splitSeries = (series: number[], shares: number[]): number[][] => {
  const parts = shares.map(() => Array<number>(series.length).fill(0));
  series.forEach((total, m) => {
    let left = total;
    shares.forEach((share, i) => {
      const part =
        i === shares.length - 1
          ? left
          : Math.round((total * share) / 100) * 100;
      parts[i][m] = part;
      left -= part;
    });
  });
  return parts;
};

/** Soleil Rouge's three parks: the agreement-level production the prototype shipped, split 45/30/25. */
const FR_DEVICE_ACTUAL = splitSeries(
  [4200, 6100, 8300, 9600, 10800, 11500, 11900],
  [0.45, 0.3, 0.25]
);

/**
 * Germany is six pay-as-produced wind agreements of roughly equal size. Together they produce
 * exactly what the prototype's single Nordsee agreement did, so every downstream figure
 * (German portfolio, pool allocation, group balance) reconciles to the same totals.
 */
const DE_ACTUAL = [2400, 9800, 9200, 7400, 6800, 7200, 7900];
const DE_FORECAST = [
  4100, 9200, 9600, 7000, 7300, 6800, 8400, 8600, 9400, 10500, 11200, 11000,
];
const DE_SHARES = [0.19, 0.17, 0.17, 0.16, 0.16, 0.15];
const DE_ACTUAL_SPLIT = splitSeries(DE_ACTUAL, DE_SHARES);
const DE_FORECAST_SPLIT = splitSeries(DE_FORECAST, DE_SHARES);
const GERMAN_AGREEMENTS: {
  key: PpaKey;
  name: string;
  counterparty: string;
  ends: string;
  devices: { id: string; name: string; share?: number }[];
}[] = [
  {
    key: "de-nordsee",
    name: "Nordsee Wind PPA",
    counterparty: "Nordsee Erzeugung GmbH · Germany · wind",
    ends: "31 December 2030",
    devices: [
      { id: "DE-WIND-04", name: "Nordsee Wind I", share: 0.6 },
      { id: "DE-WIND-11", name: "Nordsee Wind II" },
    ],
  },
  {
    key: "de-ostsee",
    name: "Ostsee Offshore PPA",
    counterparty: "Ostsee Windkraft GmbH · Germany · wind",
    ends: "31 December 2031",
    devices: [{ id: "DE-WIND-18", name: "Ostsee Bank" }],
  },
  {
    key: "de-ems",
    name: "Emsland Wind PPA",
    counterparty: "Emsland Energie GmbH · Germany · wind",
    ends: "31 December 2029",
    devices: [{ id: "DE-WIND-22", name: "Emsland Windpark" }],
  },
  {
    key: "de-weser",
    name: "Weser Wind PPA",
    counterparty: "Weser Windpark GmbH & Co. KG · Germany · wind",
    ends: "31 December 2030",
    devices: [{ id: "DE-WIND-27", name: "Weser Marsch" }],
  },
  {
    key: "de-harz",
    name: "Harz Wind PPA",
    counterparty: "Harzwind Betriebs GmbH · Germany · wind",
    ends: "31 December 2028",
    devices: [{ id: "DE-WIND-31", name: "Harz Hochfläche" }],
  },
  {
    key: "de-erzgebirge",
    name: "Erzgebirge Wind PPA",
    counterparty: "Erzgebirge Windenergie GmbH · Germany · wind",
    ends: "31 December 2031",
    devices: [{ id: "DE-WIND-36", name: "Erzgebirge Kamm" }],
  },
];
const GERMAN_PPAS = Object.fromEntries(
  GERMAN_AGREEMENTS.map((a, i) => {
    const actual = DE_ACTUAL_SPLIT[i];
    const deviceActuals =
      a.devices.length === 1
        ? [actual]
        : splitSeries(
            actual,
            a.devices.map((d) => d.share ?? 0)
          );
    const ppa: PpaInput = {
      key: a.key,
      group: "eecs",
      domain: "de",
      name: a.name,
      counterparty: a.counterparty,
      cadence: "Pay-as-produced · monthly delivery",
      system: "EECS GO",
      terms: "pay-as-produced · no fixed volume",
      ends: a.ends,
      quarterly: false,
      tech: "Wind",
      cc: "DE",
      registry: "Germany (HKNR)",
      registryCode: "DE",
      devices: a.devices.map((d, j) => ({
        id: d.id,
        name: d.name,
        actual: deviceActuals[j],
        deliveredThrough: 7,
      })),
      forecast: DE_FORECAST_SPLIT[i],
    };
    return [a.key, ppa];
  })
) as Record<Extract<PpaKey, `de-${string}`>, PpaInput>;

/** France: one portfolio per consumption month. Paris (two meters) + Lyon aggregate to the domain figure. */
export const FRANCE_MONTHS: FranceMonthInput[] = [
  {
    m: 0,
    consumption: 12400,
    locked: 0,
    flexible: 9400,
    expected: 0,
    event: "Awaiting sourcing",
  },
  {
    m: 1,
    consumption: 9200,
    locked: 9200,
    flexible: 0,
    expected: 0,
    event: "Cancelled 14 Mar 2026",
  },
  {
    m: 2,
    consumption: 9500,
    locked: 9500,
    flexible: 0,
    expected: 0,
    event: "Cancelled 11 Apr 2026",
  },
  {
    m: 3,
    consumption: 9300,
    locked: 9300,
    flexible: 0,
    expected: 0,
    event: "Cancelled 6 May 2026",
  },
  {
    m: 4,
    consumption: 9600,
    locked: 0,
    flexible: 9600,
    expected: 0,
    event: "In account",
  },
  {
    m: 5,
    consumption: 9800,
    locked: 0,
    flexible: 9800,
    expected: 0,
    event: "In account",
  },
  {
    m: 6,
    consumption: 10000,
    locked: 0,
    flexible: 10000,
    expected: 0,
    event: "In account",
  },
  {
    m: 7,
    consumption: 10200,
    locked: 0,
    flexible: 0,
    expected: 10200,
    event: "Awaiting delivery",
  },
  {
    m: 8,
    consumption: 10100,
    locked: 0,
    flexible: 0,
    expected: 10100,
    event: "Not produced yet",
  },
  {
    m: 9,
    consumption: 9900,
    locked: 0,
    flexible: 0,
    expected: 9900,
    event: "Not produced yet",
  },
  {
    m: 10,
    consumption: 9700,
    locked: 0,
    flexible: 0,
    expected: 9700,
    event: "Not produced yet",
  },
  {
    m: 11,
    consumption: 9500,
    locked: 0,
    flexible: 0,
    expected: 9500,
    event: "Not produced yet",
  },
];
export const CANCELLED_FRANCE_MONTHS = new Set([1, 2, 3]);

export const DOMAIN_INPUTS: Record<DomainKey, DomainInput> = {
  fr: {
    key: "fr",
    group: "eecs",
    name: "France",
    system: "EECS GO",
    cadence: "monthly",
    cutoff: "31 Mar 2027",
    cutoffNote: "EEX cancellation cut-off",
    rule: "Monthly · production month = consumption month",
    structure: "12 monthly portfolios",
    registry: "Live · EEX",
    registryCode: "FR",
    route: "Direct via Soldera",
    supply: 116200,
    isMonthly: true,
    earmarked: { locked: 28000, flexible: 38800, expected: 49400 },
    note: "Each consumption month is its own portfolio. A certificate earmarked to one month cannot move to another, so a surplus month never offsets a short month.",
    meters: [
      {
        id: "FR-MP-001",
        name: "Paris DC1",
        entity: "Northwind Demo France SAS",
        supplier: "Électricité Demo France SA",
        source: "Connected feed",
        monthly: FR_PARIS.map((v) => v - 2000),
      },
      {
        id: "FR-MP-007",
        name: "Paris DC2",
        entity: "Northwind Demo France SAS",
        supplier: "Électricité Demo France SA",
        source: "Connected feed",
        monthly: flat(2000),
      },
      {
        id: "FR-MP-014",
        name: "Lyon DC1",
        entity: "Northwind Demo France Data Services SAS",
        supplier: "Demo Énergie Régionale SAS",
        source: "Approved upload",
        reportedThrough: 8,
        monthly: flat(2500),
      },
    ],
    companies: [
      {
        name: "Northwind Demo France SAS",
        site: "Paris FR-01",
        beneficiaryType: "Consumer",
        registryCode: "SIREN 812 345 678",
        address: "12 rue de la Boétie, 75008 Paris, France",
        visibility: "Public statement",
        purpose: PURPOSE,
        note: "French rules let any EEX account holder cancel, so Soldera submits directly from this entity's own account with no supplier step.",
      },
      {
        name: "Northwind Demo France Data Services SAS",
        site: "Lyon FR-02",
        beneficiaryType: "Consumer",
        registryCode: "SIREN 907 654 321",
        address: "Parc du Puy d'Or, 69570 Lyon, France",
        visibility: "Public statement",
        purpose: PURPOSE,
        note: "Cancellation is direct via Soldera, as for the Paris entity. Lyon's consumption arrives as an approved bill upload rather than a meter feed.",
      },
    ],
  },
  de: {
    key: "de",
    group: "eecs",
    name: "Germany",
    system: "EECS GO",
    cadence: "monthly",
    cutoff: "Supplier-defined",
    cutoffNote: "Soldera target 31 Mar 2027",
    rule: "Annual · same calendar year",
    structure: "1 annual portfolio",
    registry: "Live · HKNR",
    registryCode: "DE",
    route: "Supplier-coordinated",
    supply: 98200,
    isMonthly: false,
    earmarked: null,
    note: "One portfolio for the whole claim year. Any 2026 production month can serve any 2026 consumption month, so monthly consumption is tracked for data quality only and creates no monthly legal failure.",
    meters: [
      {
        id: "DE-MP-002",
        name: "Frankfurt DC1",
        entity: "Northwind Demo Germany GmbH",
        supplier: "Stadtwerke Demo AG",
        source: "Connected feed",
        monthly: DE_FRANKFURT.map((v) => v - 1200),
      },
      {
        id: "DE-MP-011",
        name: "Frankfurt DC2",
        entity: "Northwind Demo Germany GmbH",
        supplier: "Mainova Demo AG",
        source: "Connected feed",
        monthly: flat(1200),
      },
      {
        id: "DE-MP-019",
        name: "Munich DC1",
        entity: "Northwind Demo Germany Cloud GmbH",
        supplier: "Stadtwerke München Demo GmbH",
        source: "Estimated from contract capacity",
        alwaysEstimated: true,
        monthly: flat(1500),
      },
    ],
    companies: [
      {
        name: "Northwind Demo Germany GmbH",
        site: "Frankfurt DE-01",
        beneficiaryType: "Consumer",
        registryCode: "HRB 148231 · Frankfurt am Main",
        address: "Hanauer Landstraße 291, 60314 Frankfurt, Germany",
        visibility: "Public statement",
        purpose: PURPOSE,
        note: "Two suppliers serve this entity's consumption locations, so its cancellation is organised as two separate instructions.",
      },
      {
        name: "Northwind Demo Germany Cloud GmbH",
        site: "Munich DE-02",
        beneficiaryType: "Consumer",
        registryCode: "",
        address: "Erika-Mann-Straße 33, 80636 Munich, Germany",
        visibility: "Public statement",
        purpose: PURPOSE,
        note: "Registry code missing and consumption is estimated from contract capacity. Both block a cancellation statement for this entity.",
      },
    ],
  },
  se: {
    key: "se",
    group: "eecs",
    name: "Sweden",
    system: "EECS GO",
    cadence: "annual",
    cutoff: "31 Mar 2027",
    cutoffNote: "Cesar cancellation cut-off",
    rule: "Annual · same calendar year",
    structure: "1 annual portfolio",
    registry: "Live · Cesar",
    registryCode: "SE",
    route: "Direct via Soldera",
    supply: 64600,
    isMonthly: false,
    earmarked: null,
    note: "One portfolio for the whole claim year. Sweden is last in the allocation order, so its whole 2026 cover is volume the agreements have not delivered yet.",
    meters: [
      {
        id: "SE-MP-003",
        name: "Stockholm DC1",
        entity: "Northwind Demo Sweden AB",
        supplier: "Vattendemo AB",
        source: "Reported",
        monthly: SE_STOCKHOLM,
      },
    ],
    companies: [
      {
        name: "Northwind Demo Sweden AB",
        site: "Stockholm SE-01",
        beneficiaryType: "Consumer",
        registryCode: "556123-4567",
        address: "Kungsgatan 12, 111 35 Stockholm, Sweden",
        visibility: "Private statement",
        purpose: PURPOSE,
        note: "Cesar supports private cancellation, so this statement is withheld from the public registry list.",
      },
    ],
  },
  es: {
    key: "es",
    group: "eecs",
    name: "Spain",
    system: "EECS GO",
    cadence: "quarterly",
    cutoff: "Supplier-defined",
    cutoffNote: "Soldera target 31 Mar 2027",
    rule: "Annual · same calendar year",
    structure: "1 annual portfolio",
    registry: "Live · CNMC",
    registryCode: "ES",
    route: "Supplier-coordinated",
    supply: 76100,
    isMonthly: false,
    earmarked: null,
    note: "One portfolio for the whole claim year. Cancellation runs through the qualifying electricity supplier.",
    meters: [
      {
        id: "ES-MP-004",
        name: "Madrid DC1",
        entity: "Northwind Demo Spain SL",
        supplier: "Iberdemo Energía SA",
        source: "Reported",
        monthly: ES_MADRID,
      },
    ],
    companies: [
      {
        name: "Northwind Demo Spain SL",
        site: "Madrid ES-01",
        beneficiaryType: "Consumer",
        registryCode: "CIF B-87654321",
        address: "Paseo de la Castellana 259, 28046 Madrid, Spain",
        visibility: "Public statement",
        purpose: PURPOSE,
        note: "Cancellation is executed by the qualifying electricity supplier.",
      },
    ],
  },
  uk: {
    key: "uk",
    group: "rego",
    name: "United Kingdom",
    system: "UK REGO",
    cadence: "monthly",
    cutoff: "31 Mar 2027",
    cutoffNote: "REGO compliance period ends",
    rule: "Annual · REGO compliance period 1 Apr – 31 Mar",
    structure: "1 annual portfolio",
    registry: "Live · Ofgem REGO",
    registryCode: "GB",
    route: "Direct via Soldera",
    supply: 80400,
    isMonthly: false,
    earmarked: null,
    note: "One portfolio for the compliance period. REGOs are a separate certificate system from EECS: they cannot be cancelled against an EECS domain, and EECS GOs cannot serve this one.",
    meters: [
      {
        id: "UK-MP-005",
        name: "London DC1",
        entity: "Northwind Demo UK Ltd",
        supplier: "Demo Energy Supply Ltd",
        source: "Connected feed",
        monthly: flat(5000),
      },
      {
        id: "UK-MP-021",
        name: "Manchester DC1",
        entity: "Northwind Demo UK Ltd",
        supplier: "Northern Demo Power Ltd",
        source: "Connected feed",
        monthly: flat(1700),
      },
    ],
    companies: [
      {
        name: "Northwind Demo UK Ltd",
        site: "London UK-01",
        beneficiaryType: "Consumer",
        registryCode: "Companies House 09876543",
        address:
          "1 Principal Place, Worship Street, London EC2A 2FA, United Kingdom",
        visibility: "Public statement",
        purpose: PURPOSE,
        note: "Two suppliers serve this entity's consumption locations, but REGO retirement runs through Soldera's Ofgem account rather than the suppliers.",
      },
    ],
  },
};

export const PPA_INPUTS: Record<PpaKey, PpaInput> = {
  fr: {
    key: "fr",
    group: "eecs",
    domain: "fr",
    name: "Soleil Rouge PPA",
    counterparty: "Soleil Rouge Énergie SAS · France · solar",
    cadence: "Pay-as-produced · monthly delivery",
    system: "EECS GO",
    terms: "pay-as-produced · no fixed volume",
    ends: "31 December 2028",
    quarterly: false,
    tech: "Solar",
    cc: "FR",
    registry: "France (EEX)",
    registryCode: "FR",
    // Three parks under one agreement. July (due 31 Aug) arrived in two transfers from PV and
    // Nord, the second of them late; Sud has not transferred July at all, so the month sits
    // partly delivered and the agreement is the open incoming item.
    devices: [
      {
        id: "FR-SOLAR-17",
        name: "Soleil Rouge PV",
        actual: FR_DEVICE_ACTUAL[0],
        deliveredThrough: 7,
        deliveredOn: { 6: "28 Aug 2026" },
      },
      {
        id: "FR-SOLAR-21",
        name: "Soleil Rouge Nord",
        actual: FR_DEVICE_ACTUAL[1],
        deliveredThrough: 7,
        deliveredOn: { 6: "3 Sep 2026" },
      },
      {
        id: "FR-SOLAR-23",
        name: "Soleil Rouge Sud",
        actual: FR_DEVICE_ACTUAL[2],
        deliveredThrough: 6,
      },
    ],
    forecast: [
      7200, 6400, 8600, 9200, 11300, 11200, 12400, 10700, 9800, 7400, 5100,
      4200,
    ],
  },
  ...GERMAN_PPAS,
  se: {
    key: "se",
    group: "eecs",
    domain: "se",
    name: "Norrskog Wind PPA",
    counterparty: "Norrskog Kraft AB · Sweden · wind",
    cadence: "Pay-as-produced · monthly delivery",
    system: "EECS GO",
    terms: "pay-as-produced · no fixed volume",
    ends: "31 December 2029",
    quarterly: false,
    tech: "Wind",
    cc: "SE",
    registry: "Sweden (Cesar)",
    registryCode: "SE",
    devices: [
      {
        id: "SE-WIND-12",
        name: "Norrskog Vindpark",
        actual: [1400, 7300, 7100, 6200, 6300, 6400, 6700],
        deliveredThrough: 7,
      },
    ],
    forecast: [
      2400, 6900, 7500, 5900, 6600, 6100, 7100, 6900, 7600, 8400, 8800, 8600,
    ],
  },
  es: {
    key: "es",
    group: "eecs",
    domain: "es",
    name: "Castilla Solar PPA",
    counterparty: "Castilla Renovables SL · Spain · solar",
    cadence: "Pay-as-produced · quarterly delivery",
    system: "EECS GO",
    terms: "pay-as-produced · no fixed volume",
    ends: "31 December 2031",
    quarterly: true,
    tech: "Solar",
    cc: "ES",
    registry: "Spain (CNMC)",
    registryCode: "ES",
    devices: [
      {
        id: "ES-SOLAR-08",
        name: "Castilla Solar I",
        actual: [1400, 5300, 6500, 7300, 8500, 7950, 7200],
        deliveredThrough: 6,
      },
    ],
    forecast: [
      1800, 5600, 6200, 6950, 8150, 8300, 7500, 8700, 8150, 6700, 5700, 5400,
    ],
  },
  uk: {
    key: "uk",
    group: "rego",
    domain: "uk",
    name: "Pennine Wind PPA",
    counterparty: "Pennine Renewables Ltd · United Kingdom · wind",
    cadence: "Pay-as-produced · quarterly delivery",
    system: "UK REGO",
    terms: "pay-as-produced · no fixed volume",
    ends: "31 March 2031",
    quarterly: true,
    tech: "Wind",
    cc: "GB",
    registry: "United Kingdom (Ofgem)",
    registryCode: "GB",
    devices: [
      {
        id: "UK-WIND-06",
        name: "Pennine Ridge",
        actual: [4700, 4300, 5000, 3700, 3100, 2900, 3300],
        deliveredThrough: 6,
      },
      {
        id: "UK-WIND-09",
        name: "Pennine Moor",
        actual: [3100, 2800, 3400, 2400, 2100, 2000, 2200],
        deliveredThrough: 6,
      },
    ],
    forecast: [
      9200, 7400, 8200, 6300, 5100, 4800, 5400, 8000, 7400, 7000, 6600, 6500,
    ],
  },
};

export const isDomainKey = (value: unknown): value is DomainKey =>
  typeof value === "string" && Object.hasOwn(DOMAIN_INPUTS, value);
export const isPpaKey = (value: unknown): value is PpaKey =>
  typeof value === "string" && Object.hasOwn(PPA_INPUTS, value);
export const isGroupKey = (value: unknown): value is GroupKey =>
  value === "eecs" || value === "rego";

export const GROUPS: Record<GroupKey, GroupMeta> = {
  eecs: {
    key: "eecs",
    title: "EECS Europe Compliance Group",
    shortTitle: "EECS Europe",
    system: "EECS GO",
  },
  rego: {
    key: "rego",
    title: "REGO Compliance Group",
    shortTitle: "UK REGO",
    system: "UK REGO",
  },
};

/** Allocation priority: France's monthly portfolios draw first, then the annual portfolios. */
export const ALLOCATION_ORDER: DomainKey[] = ["fr", "de", "es", "se", "uk"];
export const ANNUAL_PORTFOLIO_NEEDS: { key: DomainKey; need: number }[] = [
  { key: "de", need: 98200 },
  { key: "es", need: 76100 },
  { key: "se", need: 64600 },
];
export const DOMAIN_PORTFOLIO_PREFIX: Record<DomainKey, string> = {
  fr: "France",
  de: "Germany",
  se: "Sweden",
  es: "Spain",
  uk: "United Kingdom",
};

export const YEARS: YearOption[] = [
  {
    year: 2027,
    state: "Planning",
    tone: "pending",
    note: "Contracting open · no consumption yet",
    banner:
      "2027 is a planning year. Consumption forecasts and PPA entitlements are being set; nothing has been delivered or cancelled.",
  },
  {
    year: 2026,
    state: "Open",
    tone: "ok",
    note: "Current compliance year",
    banner: "",
  },
  {
    year: 2025,
    state: "No data",
    tone: "pending",
    note: "Nothing on file",
    banner: "",
  },
];

const STMT_MONTHS = [
  {
    m: 3,
    cancelled: "6 May 2026",
    received: "12 May 2026",
    verifier: "M. Bernard",
  },
  {
    m: 2,
    cancelled: "11 Apr 2026",
    received: "16 Apr 2026",
    verifier: "M. Bernard",
  },
  {
    m: 1,
    cancelled: "14 Mar 2026",
    received: "19 Mar 2026",
    verifier: "A. Thomas",
  },
];
const STMT_COMPANIES = [
  {
    company: "Northwind Demo France SAS",
    site: "Paris FR-01",
    registryCode: "SIREN 812 345 678",
    acct: "88213",
    address: "12 rue de la Boétie, 75008 Paris, France",
    meters: "FR-MP-001, FR-MP-007",
    volume: (m: number) => FR_PARIS[m],
  },
  {
    company: "Northwind Demo France Data Services SAS",
    site: "Lyon FR-02",
    registryCode: "SIREN 907 654 321",
    acct: "90765",
    address: "Parc du Puy d'Or, 69570 Lyon, France",
    meters: "FR-MP-014",
    volume: () => 2500,
  },
];

/** Six executed French statements: two companies × Feb/Mar/Apr 2026. Only the EECS group has any. */
export const STATEMENTS: StatementFixture[] = STMT_MONTHS.flatMap((mo) =>
  STMT_COMPANIES.map((c) => {
    const two = String(mo.m + 1).padStart(2, "0");
    return {
      id: `${c.acct}-${two}`,
      company: c.company,
      site: c.site,
      registryCode: c.registryCode,
      address: c.address,
      meters: c.meters,
      monthIndex: mo.m,
      period: `${MONTHS[mo.m]} 2026`,
      mwh: c.volume(mo.m),
      lot: `FR-GO-2026-${two}`,
      txn: `EEX-CX-2026-${two}-${c.acct}`,
      cancelled: mo.cancelled,
      received: mo.received,
      verification: `Verified · ${mo.verifier} · registry reference match`,
    };
  })
);

export const HISTORICAL_EVIDENCE = {
  title: "April 2026 French cancellation statement",
  issuer: "Registry cancellation statement · EEX",
  beneficiary: "Northwind Demo France SAS",
  purpose: "French local disclosure",
  periodVolume: "April 2026 · 9 300 MWh",
  site: "Paris FR-01 · meter FR-MP-001",
  lot: "FR-GO-2026-04",
  txn: "EEX-CX-2026-04-88213",
  authorized: "6 May 2026 · A. Thomas",
  received: "12 May 2026",
  verification: "Verified · M. Bernard · registry reference match",
  lineage: [
    { k: "Certificate system", v: "EECS GO · France" },
    {
      k: "Generation facilities",
      v: "Soleil Rouge PV, Nord and Sud · solar",
    },
    { k: "PPA certificate supply", v: "Soleil Rouge PPA" },
    { k: "Production period", v: "April 2026" },
    { k: "Volume", v: "9 300 MWh cancelled" },
    { k: "Lifecycle state", v: "Cancelled" },
    { k: "Matched demand", v: "Paris FR-01 · April 2026 meter reading" },
    { k: "Authorization event", v: "6 May 2026 · A. Thomas" },
    { k: "Execution reference", v: "EEX-CX-2026-04-88213" },
    { k: "Evidence", v: "EEX cancellation statement, received 12 May 2026" },
  ],
};
